import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Download,
  X,
  FileImage,
  FileText,
  File,
  Check,
  XCircle,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useBusinessUserDetails,
  useBusinessDocDecision,
  useBusinessKYCDecision,
  useBusinessContactDecision,
} from "../../../sevices/hooks/users";
import { toast } from "sonner";
import FilePreviewModal from "./FilePreviewModal";

interface Document {
  id: string;
  name: string;
  extension: string;
  originalFileName: string;
  type: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  documentType: string;
  url: string;
  status: string;
  rejectedReason: string | null;
}

interface BusinessDetailsData {
  id: string;
  refereeCode: string;
  date: string;
  lastlogin: string | null;
  businessName: string;
  businessNature: string;
  RegistrationNumber: string;
  businessType: string;
  countryOfRegistration: string;
  businessAddress: string;
  businessPhone: string;
  businessEmail: string;
  kycstatus: string;
  status: string;
  registrationDocument: string;
  memert: string | null;
  statusReport: string | null;
  shareholding: string | null;
  ContactPersonId: string;
  contactPerson: string;
  contactPersonPhoneNumber: string;
  contactPersonEmail: string;
  contactPersonIdType: string | null;
  contactPersonIdDoc: string;
  contactPersonIdDocStatus: string | null;
  email: string;
  BankName: string;
  BankAccountNumber: string;
  dateCreated: string;
  phoneNumber: string;
  businessUserKycStatus: string;
  documents: Document[];
  user?: {
    bvn?: string;
  };
}

type SectionStatus = "pending" | "approved" | "rejected";

interface RejectionReason {
  id: number;
  value: string;
  slug: string;
  label: string;
}

const rejectionReasons: RejectionReason[] = [
  {
    id: 1,
    value: "incomplete_details",
    slug: "incomplete-details",
    label: "Business Details Incomplete (missing details)",
  },
  {
    id: 2,
    value: "inconsistent_details",
    slug: "inconsistent-details",
    label: "Business Details Inconsistent",
  },
  {
    id: 3,
    value: "unverifiable_details",
    slug: "unverifiable-details",
    label: "Business Details are Unverifiable",
  },
  {
    id: 4,
    value: "unacceptable_registration",
    slug: "unacceptable-registration",
    label: "Unacceptable documents of Registration",
  },
  {
    id: 5,
    value: "unacceptable_owner_details",
    slug: "unacceptable-owner-details",
    label: "Unacceptable Business Owner Details",
  },
  {
    id: 6,
    value: "unacceptable_identification",
    slug: "unacceptable-identification",
    label: "Unacceptable Means of Identification",
  },
  {
    id: 7,
    value: "incomplete_bank_details",
    slug: "incomplete-bank-details",
    label: "Incomplete Bank Details",
  },
  {
    id: 8,
    value: "expired_identification",
    slug: "expired-identification",
    label: "Expired means of identification",
  },
];

const BusinessCustomerOnboarding: React.FC = () => {
  const [openSection, setOpenSection] = useState<number>(0);
  const [sectionStatuses, setSectionStatuses] = useState<SectionStatus[]>([
    "pending",
    "pending",
    "pending",
    "pending",
  ]);
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    data: businessDetailsResponse,
    isLoading,
    isError,
    refetch,
  } = useBusinessUserDetails(id);
  const businessDetails = businessDetailsResponse?.data
    ?.data as BusinessDetailsData | null;

  const businessDocDecisionMutation = useBusinessDocDecision();
  const businessKYCDecisionMutation = useBusinessKYCDecision();
  const businessContactDecisionMutation = useBusinessContactDecision();

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionTarget, setRejectionTarget] = useState<{
    type: "document" | "section";
    id?: string;
    section?: number;
  } | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionNote, setRejectionNote] = useState("");
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);

  // Helper function to normalize status
  const normalizeStatus = (status: string): SectionStatus => {
    const lowerStatus = status?.toLowerCase();
    if (lowerStatus === "verified" || lowerStatus === "approved")
      return "approved";
    if (lowerStatus === "rejected") return "rejected";
    return "pending";
  };

  // Helper function to calculate section status based on documents
  const calculateSectionStatus = (
    documentTypes: string[],
    fallbackStatus?: string
  ): SectionStatus => {
    if (!businessDetails) return "pending";

    // Get all documents for this section
    const sectionDocuments = documentTypes
      .map((type) => getDocumentByType(type))
      .filter((doc): doc is Document => doc !== undefined);

    // If no documents exist for this section, use fallback status (kycstatus)
    if (sectionDocuments.length === 0) {
      return fallbackStatus ? normalizeStatus(fallbackStatus) : "pending";
    }

    // If any document is rejected, section is rejected
    if (
      sectionDocuments.some((doc) => normalizeStatus(doc.status) === "rejected")
    ) {
      return "rejected";
    }

    // If any document is pending, section is pending
    if (
      sectionDocuments.some((doc) => normalizeStatus(doc.status) === "pending")
    ) {
      return "pending";
    }

    // If all documents are approved, section is approved
    if (
      sectionDocuments.every(
        (doc) => normalizeStatus(doc.status) === "approved"
      )
    ) {
      return "approved";
    }

    return "pending";
  };

  // Update section statuses when businessDetails changes
  useEffect(() => {
    if (businessDetails) {
      const newStatuses: SectionStatus[] = [
        // Section 0: Authorised Business Representative
        calculateSectionStatus(
          ["other_verification_document", "drivers_license"],
          businessDetails.kycstatus
        ),
        // Section 1: Business Registration
        calculateSectionStatus(
          [
            "business_registration",
            "articles_of_incorporation",
            "incorporation_document",
            "bank_statement",
          ],
          businessDetails.kycstatus
        ),
        // Section 2: Address and Contact (no documents)
        normalizeStatus(businessDetails.kycstatus),
        // Section 3: Ultimate Business Owner
        calculateSectionStatus(
          ["drivers_license", "other_verification_document"],
          businessDetails.contactPersonIdDocStatus || businessDetails.kycstatus
        ),
      ];
      setSectionStatuses(newStatuses);
    }
  }, [businessDetails]);

  useEffect(() => {
    setOpenSection(0);
  }, []);

  const getDocumentByType = (type: string): Document | undefined => {
    return businessDetails?.documents?.find((doc) => doc.documentType === type);
  };

  const parseContactPersonName = () => {
    const fullName = businessDetails?.contactPerson || "";
    const nameParts = fullName.trim().split(/\s+/);
    return {
      firstName: nameParts[0] || "NA",
      lastName: nameParts.slice(1).join(" ") || "NA",
    };
  };

  const parseAddress = () => {
    const address = businessDetails?.businessAddress || "";
    const parts = address.split(",").map((part) => part.trim());
    return {
      street: parts[0] || "NA",
      city: parts.length > 2 ? parts[2] : "NA",
      state: parts.length > 2 ? parts[2] : "NA",
      country: parts.length > 4 ? parts[4] : "NA",
      postalCode: parts.length > 3 ? parts[3] : "NA",
    };
  };

  const getFileIcon = (extension: string) => {
    const ext = extension?.toLowerCase();
    const iconProps = { className: "w-8 h-8" };
    if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) {
      return <FileImage {...iconProps} className="w-8 h-8 text-blue-500" />;
    } else if (ext === "pdf") {
      return <FileText {...iconProps} className="w-8 h-8 text-red-500" />;
    } else {
      return <File {...iconProps} className="w-8 h-8 text-gray-500" />;
    }
  };

  const toggleSection = (index: number) => {
    setOpenSection(openSection === index ? -1 : index);
  };

  const handleApprove = (sectionIndex: number) => {
    const newStatuses = [...sectionStatuses];
    newStatuses[sectionIndex] = "approved";
    setSectionStatuses(newStatuses);
    if (sectionIndex < 3) {
      setOpenSection(sectionIndex + 1);
    }
  };

  const handleReject = (sectionIndex: number) => {
    setRejectionTarget({ type: "section", section: sectionIndex });
    setShowRejectModal(true);
  };

  const handleRejectAll = () => {
    toast.error("Reject all functionality coming soon");
  };

  const handleApproveDocument = async (document: Document) => {
    try {
      await businessDocDecisionMutation.mutateAsync({
        documentId: document.id,
        status: "verified",
        rejectionReason: "",
      });
      toast.success("Document approved successfully");
      refetch();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to approve document"
      );
    }
  };

  const handleRejectDocument = (document: Document) => {
    setRejectionTarget({ type: "document", id: document.id });
    setShowRejectModal(true);
  };

  const handleApproveKYC = async (businessId: string) => {
    console.log("Approving KYC for business ID:", businessId);
    try {
      await businessKYCDecisionMutation.mutateAsync({
        businessId,
        kycStatus: "verified",
      });
      toast.success("KYC approved successfully");
      refetch();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to approve KYC");
    }
  };

  const handleRejectKYC = (businessId: string) => {
    setRejectionTarget({ type: "section", section: 0, id: businessId });
    setShowRejectModal(true);
  };

  const handleApproveContact = async (businessContactId: string) => {
    console.log("Approving contact for ID:", businessContactId);
    try {
      await businessContactDecisionMutation.mutateAsync({
        businessContactId,
        kycStatus: "verified",
      });
      toast.success("Contact approved successfully");
      refetch();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to approve contact"
      );
    }
  };

  const handleRejectContact = (businessContactId: string) => {
    setRejectionTarget({ type: "section", section: 2, id: businessContactId });
    setShowRejectModal(true);
  };

  const confirmRejection = async () => {
    if (!rejectionReason) {
      toast.error("Please select a rejection reason");
      return;
    }
    console.log("Rejection target:", rejectionTarget);
    console.log(rejectionReason, rejectionNote, "mmmmmm");
    try {
      if (rejectionTarget?.type === "document" && rejectionTarget.id) {
        await businessDocDecisionMutation.mutateAsync({
          documentId: rejectionTarget.id,
          status: "rejected",
          rejectionReason,
        });
        toast.success("Document rejected successfully");
      } else if (rejectionTarget?.type === "section" && rejectionTarget.id) {
        if (rejectionTarget.section === 0) {
          await businessKYCDecisionMutation.mutateAsync({
            businessId: rejectionTarget.id,
            kycStatus: "rejected",
          });
          toast.success("KYC rejected successfully");
        } else if (rejectionTarget.section === 2) {
          await businessContactDecisionMutation.mutateAsync({
            businessContactId: rejectionTarget.id,
            kycStatus: "rejected",
          });
          toast.success("Contact rejected successfully");
        }
      }

      refetch();
      setShowRejectModal(false);
      setRejectionTarget(null);
      setRejectionReason("");
      setRejectionNote("");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to reject");
    }
  };

  const cancelRejection = () => {
    setShowRejectModal(false);
    setRejectionTarget(null);
    setRejectionReason("");
    setRejectionNote("");
  };

  const handleApproveAll = () => {
    setSectionStatuses(["approved", "approved", "approved", "approved"]);
  };

  const handleDownloadDocument = (document: Document) => {
    window.open(document.url, "_blank");
  };

  const StatusBadge: React.FC<{ status: SectionStatus }> = ({ status }) => {
    const styles = {
      pending:
        "bg-[#FEF3E2] text-[#FF9636] text-[10px] px-2 py-1 rounded-full font-medium",
      approved:
        "bg-emerald-100 text-[#0eb683] text-[10px] px-2 py-1 rounded-full font-medium",
      rejected:
        "bg-red-100 text-red-600 text-[10px] px-2 py-1 rounded-full font-medium",
    };
    return (
      <span className={styles[status]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const SkeletonLoader: React.FC = () => (
    <div className="animate-pulse">
      <div className="flex justify-between items-center mb-4">
        <div className="h-6 bg-gray-200 rounded w-48"></div>
        <div className="flex gap-3">
          <div className="h-10 bg-gray-200 rounded-2xl w-28"></div>
          <div className="h-10 bg-gray-200 rounded-2xl w-28"></div>
        </div>
      </div>
      <div className="space-y-4">
        {[1, 2, 3, 4].map((item) => (
          <div
            key={item}
            className="border border-[#E6E6E6] rounded-xl overflow-hidden"
          >
            <div className="w-full px-6 py-5 flex items-center justify-between bg-white">
              <div className="flex items-center gap-3">
                <div className="h-5 bg-gray-200 rounded w-64"></div>
                <div className="h-6 bg-gray-200 rounded-full w-16"></div>
              </div>
              <div className="h-5 w-5 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const DocumentPreview: React.FC<{
    document: Document | undefined;
    showActions?: boolean;
  }> = ({ document, showActions = false }) => {
    if (!document) {
      return (
        <div className="mt-2 text-sm text-gray-500 italic">
          No document available
        </div>
      );
    }

    const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(
      document.extension?.toLowerCase()
    );
    const getDocumentStatus = (): SectionStatus => {
      return normalizeStatus(document.status);
    };

    return (
      <div className="mt-2">
        <div className="inline-flex items-center gap-3 p-3 border border-[#E6E6E6] rounded-xl w-full">
          <div className="flex-shrink-0">
            {isImage ? (
              <div className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200">
                <img
                  src={document.url}
                  alt={document.originalFileName}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-lg bg-gray-50 flex items-center justify-center">
                {getFileIcon(document.extension)}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {document.originalFileName}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs text-gray-500 uppercase">
                {document.extension} File
              </p>
              <StatusBadge status={getDocumentStatus()} />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setPreviewDocument(document)}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap"
            >
              View
            </button>
            {showActions && normalizeStatus(document.status) !== "approved" && (
              <>
                <button
                  onClick={() => handleApproveDocument(document)}
                  className="p-2 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
                  title="Approve"
                >
                  <Check className="w-4 h-4 text-emerald-600" />
                </button>
                <button
                  onClick={() => handleRejectDocument(document)}
                  className="p-2 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
                  title="Reject"
                >
                  <XCircle className="w-4 h-4 text-red-600" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#fff] p-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-2 flex items-center gap-3">
            <div className="bg-[#E5F2EA] border rounded-full p-1">
              <ChevronLeft className="w-5 h-5" />
            </div>
            <span className="text-[12px] text-[#5C6F7F] font-medium tracking-wide">
              Customer Onboarding
            </span>
          </div>
          <div className="bg-white">
            <SkeletonLoader />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !businessDetails) {
    return (
      <div className="min-h-screen bg-[#fff] p-6 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg font-medium mb-4">
            Error loading business details
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const { firstName, lastName } = parseContactPersonName();
  const addressParts = parseAddress();

  return (
    <div className="min-h-screen bg-[#fff] p-6">
      <div className="">
        <div className="mb-2 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="bg-[#E5F2EA] border rounded-full p-1 transition-all hover:bg-[#d0e7dc]"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-[12px] text-[#5C6F7F] font-medium tracking-wide">
            Customer Onboarding
          </span>
        </div>

        <div className="bg-white">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-lg font-bold text-[#2B2B2B]">
              {businessDetails.businessName}
            </h1>
            {/* <div className="flex gap-3">
              <button
                onClick={handleRejectAll}
                className="px-6 py-[8px] border border-teal-600 text-teal-600 rounded-2xl font-normal hover:bg-teal-50 transition-colors"
              >
                Reject all
              </button>
              <button
                onClick={handleApproveAll}
                className="px-6 py-[8px] bg-[#0eb683] text-white rounded-2xl font-normal hover:bg-emerald-700 transition-colors"
              >
                Approve all
              </button>
            </div> */}
          </div>

          <div className="space-y-4">
            {/* Authorised Business Representative */}
            <div className="border border-[#E6E6E6] rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection(0)}
                className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <h2 className="font-semibold text-[#071827]">
                    Authorised business representative
                  </h2>
                  <StatusBadge status={sectionStatuses[0]} />
                </div>
                {openSection === 0 ? (
                  <ChevronUp className="w-5 h-5 text-gray-600" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-600" />
                )}
              </button>

              {openSection === 0 && (
                <div className="px-6 pb-6 bg-white">
                  <div className="pt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                      <div>
                        <span className="text-sm text-gray-500">
                          First Name :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">
                          {firstName}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">
                          Last Name :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">
                          {lastName}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">
                          Date of Birth :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">NA</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">
                          Phone Number :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">
                          {businessDetails.contactPersonPhoneNumber || "NA"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">BVN :</span>
                        <p className="text-gray-900 font-medium mt-1">
                          {businessDetails.user?.bvn || "NA"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">NIN :</span>
                        <p className="text-gray-900 font-medium mt-1">NA</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-sm text-gray-500">
                          Email Address :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">
                          {businessDetails.contactPersonEmail || "NA"}
                        </p>
                      </div>
                    </div>

                    <div className="pt-4">
                      <span className="text-sm text-gray-500">
                        Means of Identification as business employee :
                      </span>
                      <DocumentPreview
                        document={getDocumentByType(
                          "other_verification_document"
                        )}
                        showActions={true}
                      />
                    </div>

                    <div className="pt-4">
                      <div className="flex items-center gap-2">
                        <span className="text-base font-semibold text-gray-900">
                          Means of Identification
                        </span>
                      </div>
                      <div className="mt-3">
                        <span className="text-sm text-gray-500">ID Type :</span>
                        <p className="text-gray-900 font-medium mt-1">
                          {businessDetails.contactPersonIdType ||
                            "Driver's License"}
                        </p>
                      </div>
                      <DocumentPreview
                        document={getDocumentByType("drivers_license")}
                        showActions={true}
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-6">
                      <button
                        onClick={() => handleRejectKYC(businessDetails.id)}
                        className="px-6 py-[8px] border border-teal-600 text-teal-600 rounded-2xl font-normal hover:bg-teal-50 transition-colors"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => handleApproveKYC(businessDetails.id)}
                        className="px-6 py-[8px] bg-[#0eb683] text-white rounded-2xl font-normal hover:bg-emerald-700 transition-colors"
                      >
                        Approve
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Business Registration */}
            <div className="border border-[#E6E6E6] rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection(1)}
                className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <h2 className="font-semibold text-[#071827]">
                    Business registration
                  </h2>
                  <StatusBadge status={sectionStatuses[1]} />
                </div>
                {openSection === 1 ? (
                  <ChevronUp className="w-5 h-5 text-gray-600" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-600" />
                )}
              </button>

              {openSection === 1 && (
                <div className="px-6 pb-6 bg-white">
                  <div className="pt-4 space-y-4">
                    <div>
                      <span className="text-sm text-gray-500">
                        Business Name :
                      </span>
                      <p className="text-gray-900 font-medium mt-1">
                        {businessDetails.businessName}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">
                        Business Nature :
                      </span>
                      <p className="text-gray-900 mt-1 leading-relaxed">
                        {businessDetails.businessNature || "NA"}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                      <div>
                        <span className="text-sm text-gray-500">
                          Business Type :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">
                          {businessDetails.businessType || "NA"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">
                          Registration Number :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">
                          {businessDetails.RegistrationNumber || "NA"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">
                          Tax Reference Number :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">NA</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">
                          Country of Registration :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">
                          {businessDetails.countryOfRegistration || "NA"}
                        </p>
                      </div>
                    </div>

                    <div className="pt-4">
                      <span className="text-sm text-gray-500">
                        Registration documents (CAC incorporation certificates,
                        etc.) :
                      </span>
                      <DocumentPreview
                        document={getDocumentByType("business_registration")}
                        showActions={true}
                      />
                    </div>

                    <div className="pt-4">
                      <span className="text-sm text-gray-500">
                        Articles of Incorporation :
                      </span>
                      <DocumentPreview
                        document={getDocumentByType(
                          "articles_of_incorporation"
                        )}
                        showActions={true}
                      />
                    </div>

                    <div className="pt-4">
                      <span className="text-sm text-gray-500">
                        Incorporation Document :
                      </span>
                      <DocumentPreview
                        document={getDocumentByType("incorporation_document")}
                        showActions={true}
                      />
                    </div>

                    <div className="pt-4">
                      <span className="text-sm text-gray-500">
                        Bank Statement (Not older than 3 months) :
                      </span>
                      <DocumentPreview
                        document={getDocumentByType("bank_statement")}
                        showActions={true}
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-6">
                      <button
                        onClick={() => handleReject(1)}
                        className="px-6 py-[8px] border border-teal-600 text-teal-600 rounded-2xl font-normal hover:bg-teal-50 transition-colors"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => handleApprove(1)}
                        className="px-6 py-[8px] bg-[#0eb683] text-white rounded-2xl font-normal hover:bg-emerald-700 transition-colors"
                      >
                        Approve
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Address and Contact */}
            <div className="border border-[#E6E6E6] rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection(2)}
                className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <h2 className="font-semibold text-[#071827]">
                    Address and contact
                  </h2>
                  <StatusBadge status={sectionStatuses[2]} />
                </div>
                {openSection === 2 ? (
                  <ChevronUp className="w-5 h-5 text-gray-600" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-600" />
                )}
              </button>

              {openSection === 2 && (
                <div className="px-6 pb-6 bg-white">
                  <div className="pt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                      <div className="col-span-2">
                        <span className="text-sm text-gray-500">
                          Business Address :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">
                          {businessDetails.businessAddress || "NA"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">City :</span>
                        <p className="text-gray-900 font-medium mt-1">
                          {addressParts.city}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">State :</span>
                        <p className="text-gray-900 font-medium mt-1">
                          {addressParts.state}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Country :</span>
                        <p className="text-gray-900 font-medium mt-1">
                          {addressParts.country}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">
                          Postal Code :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">
                          {addressParts.postalCode}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">
                          Phone Number :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">
                          {businessDetails.businessPhone || "NA"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Email :</span>
                        <p className="text-gray-900 font-medium mt-1">
                          {businessDetails.businessEmail || "NA"}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-6">
                      <button
                        onClick={() => handleReject(2)}
                        className="px-6 py-[8px] border border-teal-600 text-teal-600 rounded-2xl font-normal hover:bg-teal-50 transition-colors"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => handleApprove(2)}
                        className="px-6 py-[8px] bg-[#0eb683] text-white rounded-2xl font-normal hover:bg-emerald-700 transition-colors"
                      >
                        Approve
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Ultimate Business Owner */}
            <div className="border border-[#E6E6E6] rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection(3)}
                className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <h2 className="font-semibold text-[#071827]">
                    Ultimate business owner
                  </h2>
                  <StatusBadge status={sectionStatuses[3]} />
                </div>
                {openSection === 3 ? (
                  <ChevronUp className="w-5 h-5 text-gray-600" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-600" />
                )}
              </button>

              {openSection === 3 && (
                <div className="px-6 pb-6 bg-white">
                  <div className="pt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                      <div>
                        <span className="text-sm text-gray-500">
                          First Name :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">
                          {firstName}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">
                          Last Name :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">
                          {lastName}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">
                          Date of Birth :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">NA</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">
                          Nationality :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">NA</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">ID Type :</span>
                        <p className="text-gray-900 font-medium mt-1">
                          {businessDetails.contactPersonIdType ||
                            "Driver's License"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">
                          ID Number :
                        </span>
                        <p className="text-gray-900 font-medium mt-1">NA</p>
                      </div>
                    </div>

                    <div className="pt-4">
                      <span className="text-sm text-gray-500">
                        ID Document :
                      </span>
                      <DocumentPreview
                        document={getDocumentByType("drivers_license")}
                        showActions={true}
                      />
                    </div>

                    <div className="pt-4">
                      <span className="text-sm text-gray-500">
                        Proof of Address :
                      </span>
                      <DocumentPreview
                        document={getDocumentByType(
                          "other_verification_document"
                        )}
                        showActions={true}
                      />
                    </div>

                    {/* <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-6">
                      <button
                        onClick={() =>
                          handleRejectContact(businessDetails.ContactPersonId)
                        }
                        className="px-6 py-[8px] border border-teal-600 text-teal-600 rounded-2xl font-normal hover:bg-teal-50 transition-colors"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => {
                          handleApproveContact(businessDetails.ContactPersonId);
                        }}
                        className="px-6 py-[8px] bg-[#0eb683] text-white rounded-2xl font-normal hover:bg-emerald-700 transition-colors"
                      >
                        Approve
                      </button>
                    </div> */}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Document Preview Modal */}
      {/* {previewDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex-1 flex items-center gap-3">
                <div className="flex-shrink-0">
                  {getFileIcon(previewDocument.extension)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {previewDocument.originalFileName}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {previewDocument.extension.toUpperCase()} •{" "}
                    {previewDocument.type}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownloadDocument(previewDocument)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Download"
                >
                  <Download className="w-5 h-5 text-gray-600" />
                </button>
                <button
                  onClick={() => setPreviewDocument(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Close"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4 bg-gray-50">
              {["jpg", "jpeg", "png", "gif", "webp"].includes(
                previewDocument.extension?.toLowerCase()
              ) ? (
                <img
                  src={previewDocument.url}
                  alt={previewDocument.originalFileName}
                  className="max-w-full h-auto mx-auto rounded-lg shadow-lg"
                />
              ) : previewDocument.extension?.toLowerCase() === "pdf" ? (
                <iframe
                  src={previewDocument.url}
                  className="w-full h-full min-h-[600px] rounded-lg shadow-lg"
                  title={previewDocument.originalFileName}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full">
                  {getFileIcon(previewDocument.extension)}
                  <p className="text-gray-600 mb-4 mt-4 text-lg">
                    Preview not available for this file type
                  </p>
                  <button
                    onClick={() => handleDownloadDocument(previewDocument)}
                    className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )} */}

      {/* Robust Document Preview Modal */}
      <FilePreviewModal
        previewDocument={previewDocument}
        //@ts-ignore
        setPreviewDocument={setPreviewDocument}
        //@ts-ignore
        handleDownloadDocument={handleDownloadDocument}
      />

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Reject KYC Requirement
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                You're rejecting this submission. Please select a reason and add
                an optional note for the user.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    <span className="text-red-500">*</span>Why are you rejecting
                    this submission?
                  </label>
                  <div className="relative">
                    <select
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-2xl appearance-none focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900"
                    >
                      <option value="">Reason for rejection</option>
                      {rejectionReasons.map((reason) => (
                        <option key={reason.id} value={reason.value}>
                          {reason.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <textarea
                    value={rejectionNote}
                    onChange={(e) => setRejectionNote(e.target.value)}
                    placeholder="Add a note (optional)"
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-50 border border-[#E6E6E6] rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-gray-900 placeholder-gray-400"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={cancelRejection}
                  className="flex-1 px-6 py-3 text-teal-600 font-medium hover:bg-gray-50 rounded-2xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRejection}
                  disabled={!rejectionReason}
                  className="flex-1 px-6 py-3 bg-red-400 text-white font-medium rounded-2xl hover:bg-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessCustomerOnboarding;
